import { useSyncExternalStore } from 'react';

/**
 * Service Worker 更新 (PWA prompt 方式) を main.tsx の登録コードと React UI の間で橋渡しする
 * モジュールレベルストア (任意更新ボタン対応、2026-06-12)。
 *
 * 背景: SW 登録は main.tsx 冒頭で `registerSW({ immediate: true })` により React tree の外で行う。
 * 従来 (`registerType: 'autoUpdate'`) は `onNeedRefresh` で即 `updateSW(true)` し**強制リロード**して
 * いたが、操作中のユーザーを中断するため **prompt 方式**に変更した。新版検出を本ストアに通知し、
 * `<UpdateBanner>` が購読して「更新」ボタンを表示、押下で `applyUpdate()` が実際の更新を行う。
 *
 * 「久しぶりに開いた」ケースは、待機中の新 SW が**フレッシュ起動時に自動有効化**されるため、
 * 本バナーは主に「開いたまま使っている最中に新版が出た」セッション向け (取り残しは起きない)。
 */

type Listener = () => void;

/** 新版 SW が利用可能か (onNeedRefresh で true)。 */
let needRefresh = false;
/** main.tsx の registerSW が返す updateSW (ボタン押下時に呼ぶ)。 */
let updateFn: ((reloadPage?: boolean) => Promise<void>) | null = null;
const listeners = new Set<Listener>();

function emit(): void {
  for (const l of [...listeners]) {
    try {
      l();
    } catch {
      /* 1 つの listener 失敗を他に波及させない */
    }
  }
}

/** main.tsx の registerSW が返す updateSW を保持する (ボタン押下時に呼ぶため)。 */
export function setUpdateSW(fn: (reloadPage?: boolean) => Promise<void>): void {
  updateFn = fn;
}

/**
 * 新版 SW 検出時に main.tsx の `onNeedRefresh` から呼ぶ。バナー表示をトリガする。
 * 二重通知は no-op (既に needRefresh=true なら何もしない)。
 */
export function notifyNeedRefresh(): void {
  if (needRefresh) return;
  needRefresh = true;
  emit();
}

/**
 * 「更新」ボタン押下: 待機中の新 SW を skipWaiting + reload で適用する。
 * updateSW 未登録 (登録前/非対応) のときはフォールバックで通常リロード。
 */
export function applyUpdate(): void {
  if (updateFn) {
    void updateFn(true);
  } else if (typeof window !== 'undefined') {
    window.location.reload();
  }
}

function subscribe(listener: Listener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function getSnapshot(): boolean {
  return needRefresh;
}

/** SSR / 初期スナップショット (常に false、本アプリは CSR のみだが useSyncExternalStore の契約上必要)。 */
function getServerSnapshot(): boolean {
  return false;
}

/** 新版あり (needRefresh) を購読する React hook。`<UpdateBanner>` が使用。 */
export function useSwUpdateAvailable(): boolean {
  return useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
}

/** test 用: 内部状態をリセット。 */
export function __resetSwUpdate(): void {
  needRefresh = false;
  updateFn = null;
  listeners.clear();
}
